import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { isNative } from '../services/platform';
import {
  isBiometricAvailable,
  isBiometricLoginEnabled,
  performBiometricLogin,
  saveCredentials,
  getBiometryName,
} from '../services/biometrics';
import Push2FAPrompt from '../components/Push2FAPrompt';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [pushChallenge, setPushChallenge] = useState(null); // { challenge_token, number_code, ttl_seconds }
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometryName, setBiometryName] = useState('Biometrie');
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Check biometric availability on mount
  useEffect(() => {
    const checkBiometrics = async () => {
      if (isNative()) {
        const { available, biometryType } = await isBiometricAvailable();
        setBiometricAvailable(available);
        if (available) {
          setBiometryName(getBiometryName(biometryType));
          const enabled = await isBiometricLoginEnabled();
          setBiometricEnabled(enabled);
        }
      }
    };
    checkBiometrics();
  }, []);

  const handleLoginSuccess = async (result, credentials) => {
    if (result.success) {
      toast.success('Erfolgreich angemeldet');
      // If biometric is available but not yet enabled, ask to enable
      if (biometricAvailable && !biometricEnabled && credentials) {
        setPendingCredentials(credentials);
        setShowBiometricPrompt(true);
      } else {
        navigate('/');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const credentials = { email, password, twofa_code: requires2FA ? twoFACode : undefined };
      const result = await login(credentials);

      if (result.requires_push_2fa) {
        setPushChallenge({
          challenge_token: result.challenge_token,
          number_code: result.number_code,
          ttl_seconds: result.ttl_seconds || 90,
        });
        toast.info('Bestätige die Anmeldung in deiner ValuePlus App');
      } else if (result.requires_2fa) {
        setRequires2FA(true);
        toast.info('Bitte 2FA-Code eingeben');
      } else {
        await handleLoginSuccess(result, { email, password });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const completePushLogin = async () => {
    if (!pushChallenge) return;
    setLoading(true);
    try {
      const result = await login({
        email,
        password,
        push_challenge_token: pushChallenge.challenge_token,
      });
      if (result.success) {
        setPushChallenge(null);
        await handleLoginSuccess(result, { email, password });
      } else {
        toast.error(result.message || 'Anmeldung fehlgeschlagen');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const loginWithRecoveryCode = async (code) => {
    setLoading(true);
    try {
      const result = await login({ email, password, recovery_code: code });
      if (result.success) {
        setPushChallenge(null);
        setRequires2FA(false);
        toast.success('Mit Wiederherstellungscode angemeldet');
        await handleLoginSuccess(result, { email, password });
      } else {
        toast.error('Ungültiger Wiederherstellungscode');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ungültiger Wiederherstellungscode');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const credentials = await performBiometricLogin();
      const result = await login(credentials);
      if (result.requires_2fa) {
        setEmail(credentials.email);
        setRequires2FA(true);
        toast.info('Bitte 2FA-Code eingeben');
      } else if (result.success) {
        toast.success('Erfolgreich angemeldet');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.message || 'Biometrische Anmeldung fehlgeschlagen');
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleEnableBiometric = async () => {
    if (pendingCredentials) {
      const saved = await saveCredentials(pendingCredentials.email, pendingCredentials.password);
      if (saved) {
        toast.success(`${biometryName} Login aktiviert`);
      }
    }
    setShowBiometricPrompt(false);
    navigate('/');
  };

  const handleSkipBiometric = () => {
    setShowBiometricPrompt(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{
        background: 'linear-gradient(145deg, #0A0A0A 0%, #1a1a1a 50%, #0A0A0A 100%)'
      }}>
        {/* Decorative elements */}
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C5A059' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
        
        {/* Gold accent line */}
        <div className="absolute left-0 top-0 bottom-0 w-1" style={{
          background: 'linear-gradient(180deg, transparent 0%, #C5A059 50%, transparent 100%)'
        }}></div>
        
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          {/* Logo with dark background for visibility */}
          <div className="mb-8 p-6 rounded-2xl" style={{
            background: 'linear-gradient(145deg, #1a1a1a, #2a2a2a)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)'
          }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_29d23b9a-c758-4d5b-a3ec-a84a3acd0754/artifacts/883asqj8_image.png" 
              alt="Value Plus Logo" 
              className="h-20 w-auto"
              data-testid="login-logo"
            />
          </div>
          
          <h1 className="text-4xl font-heading font-bold text-white mb-4 text-center">
            Investor Portal
          </h1>
          
          <div className="w-16 h-1 mb-6" style={{ backgroundColor: '#C5A059' }}></div>
          
          <p className="text-gray-400 text-center max-w-md text-lg leading-relaxed">
            Exklusive Vermögensverwaltung für anspruchsvolle Investoren
          </p>
          
          {/* Feature highlights */}
          <div className="mt-12 space-y-4">
            <div className="flex items-center space-x-3 text-gray-300">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(197, 160, 89, 0.2)' }}>
                <svg className="w-4 h-4" style={{ color: '#C5A059' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span>Attraktive Renditen bis 9,7% p.a.</span>
            </div>
            <div className="flex items-center space-x-3 text-gray-300">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(197, 160, 89, 0.2)' }}>
                <svg className="w-4 h-4" style={{ color: '#C5A059' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span>Monatliche oder jährliche Auszahlungen</span>
            </div>
            <div className="flex items-center space-x-3 text-gray-300">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(197, 160, 89, 0.2)' }}>
                <svg className="w-4 h-4" style={{ color: '#C5A059' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span>Volle Transparenz über Ihr Investment</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8 sm:py-12 min-h-screen lg:min-h-0" style={{
        background: 'linear-gradient(180deg, #FAFAFA 0%, #F4F4F5 100%)'
      }}>
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-6 sm:mb-8 text-center">
            <div className="inline-block p-3 sm:p-4 rounded-xl mb-3 sm:mb-4" style={{ backgroundColor: '#0A0A0A' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_29d23b9a-c758-4d5b-a3ec-a84a3acd0754/artifacts/883asqj8_image.png" 
                alt="Value Plus Logo" 
                className="h-10 sm:h-12 w-auto"
              />
            </div>
            <h1 className="font-heading text-xl sm:text-2xl text-primary">Investor Portal</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Value Plus GmbH</p>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10" style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.03)'
          }}>
            <div className="mb-6 sm:mb-8">
              <h2 className="font-heading text-xl sm:text-2xl text-primary mb-1.5 sm:mb-2">Anmelden</h2>
              <p className="text-muted-foreground text-xs sm:text-sm">Willkommen zurück. Bitte geben Sie Ihre Zugangsdaten ein.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {pushChallenge ? (
                <Push2FAPrompt
                  challengeToken={pushChallenge.challenge_token}
                  numberCode={pushChallenge.number_code}
                  ttlSeconds={pushChallenge.ttl_seconds}
                  onApproved={completePushLogin}
                  onDenied={() => { setPushChallenge(null); toast.error('Anmeldung abgelehnt'); }}
                  onExpired={() => { setPushChallenge(null); toast.error('Anfrage abgelaufen — bitte neu starten'); }}
                  onCancel={() => setPushChallenge(null)}
                  recoveryCodeMode={true}
                  onRecoveryCode={loginWithRecoveryCode}
                />
              ) : (
              <>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-700">E-Mail Adresse</label>
                <input
                  id="email"
                  type="email"
                  className="input-field"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={requires2FA}
                  data-testid="login-email-input"
                  placeholder="ihre@email.de"
                  style={{
                    padding: '0.875rem 1rem',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-2 text-gray-700">Passwort</label>
                <input
                  id="password"
                  type="password"
                  className="input-field"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={requires2FA}
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  style={{
                    padding: '0.875rem 1rem',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              {requires2FA && (
                <div className="pt-4 mt-4" style={{ borderTop: '1px solid #E4E4E7' }}>
                  <label htmlFor="twofa" className="block text-sm font-medium mb-2 text-gray-700">
                    2FA-Authentifizierung
                  </label>
                  <input
                    id="twofa"
                    type="text"
                    className="input-field text-center text-2xl tracking-widest font-mono"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value)}
                    required
                    placeholder="000000"
                    maxLength={6}
                    data-testid="login-2fa-input"
                    style={{
                      letterSpacing: '0.5em',
                      padding: '1rem'
                    }}
                  />
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    6-stelliger Code aus Ihrer Authenticator-App
                  </p>
                </div>
              )}

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="login-submit-button"
                style={{
                  padding: '0.875rem',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  marginTop: '1.5rem'
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Wird angemeldet...
                  </span>
                ) : 'Anmelden'}
              </button>

              {/* Biometric Login Button */}
              {biometricAvailable && biometricEnabled && !requires2FA && (
                <div className="mt-4">
                  <div className="relative flex items-center justify-center my-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <span className="relative bg-white px-3 text-xs text-gray-400 uppercase tracking-wider">oder</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                    data-testid="biometric-login-button"
                    className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border-2 border-gray-200 hover:border-[#C5A059] hover:bg-amber-50 transition-all duration-200"
                    style={{ fontWeight: 500 }}
                  >
                    {biometricLoading ? (
                      <svg className="animate-spin h-5 w-5 text-[#C5A059]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-[#C5A059]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                      </svg>
                    )}
                    <span className="text-gray-700">
                      {biometricLoading ? 'Wird verifiziert...' : `Mit ${biometryName} anmelden`}
                    </span>
                  </button>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-muted-foreground mb-3">Noch kein Konto?</p>
                <a 
                  href="/registrieren" 
                  className="inline-block px-6 py-2.5 border-2 border-[#C5A059] text-[#C5A059] font-semibold rounded-lg hover:bg-[#C5A059] hover:text-white transition-colors"
                  data-testid="register-link"
                >
                  Jetzt als Investor registrieren
                </a>
              </div>
              </>
              )}
            </form>
          </div>

          <div className="mt-8 text-center text-xs text-muted-foreground">
            <p>© 2026 Value Plus GmbH. Alle Rechte vorbehalten.</p>
            <p className="mt-1 opacity-75">Sichere Verbindung über SSL verschlüsselt</p>
          </div>
        </div>
      </div>

      {/* Biometric Enable Prompt Modal */}
      {showBiometricPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl" style={{
            animation: 'fadeInUp 0.3s ease-out'
          }}>
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(197, 160, 89, 0.15)' }}>
                <svg className="w-8 h-8 text-[#C5A059]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{biometryName} aktivieren?</h3>
              <p className="text-sm text-gray-500 mb-6">
                Möchten Sie sich zukünftig schnell und sicher mit {biometryName} anmelden?
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleEnableBiometric}
                  className="btn-primary w-full"
                  style={{ padding: '0.75rem', fontSize: '0.9rem', fontWeight: 600 }}
                >
                  Ja, {biometryName} aktivieren
                </button>
                <button
                  onClick={handleSkipBiometric}
                  className="w-full py-2.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Nein, später
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
